import { Tooltip, Typography } from "@mui/material";
import type { GridApiPremium, GridColDef, GridDataSource } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { GRID_DEFAULT_PAGE_SIZE } from "@entrilia/shared/components/grid/gridHelper";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { SentEmail } from "../../../api/types/emailsTypes";
import EmailStatus from "../../tables/EmailStatus";
import { RecipientCell } from "./emailDeliveryLogGridProvider";

const columnDefinitions: GridColDef<SentEmail>[] = [
  {
    field: "recipientName",
    headerName: "Recipient",
    flex: 2,
    minWidth: 240,
    renderCell: ({ row }) => <RecipientCell recipientName={row.recipientName ?? ""} email={row.email ?? ""} />,
  },
  {
    field: "subject",
    headerName: "Subject",
    flex: 3,
    minWidth: 240,
    renderCell: ({ row }) => (
      <Tooltip title={row.subject}>
        <Typography noWrap>{row.subject}</Typography>
      </Tooltip>
    ),
  },
  {
    field: "source",
    headerName: "Source",
    flex: 2,
    minWidth: 160,
    renderCell: ({ row }) => (
      <Tooltip title={row.source}>
        <Typography noWrap>{row.source}</Typography>
      </Tooltip>
    ),
  },
  {
    field: "sentAt",
    headerName: "Sent",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => (
      <Typography variant="caption" color="textSecondary">
        {convertISODateTime(row.sentAt)}
      </Typography>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 112,
    renderCell: ({ row }) => (
      <EmailStatus
        status={row.status}
        deliveryFailureMessage={row.deliveryFailureMessage}
        deliveryFailureMessageExplanation={row.deliveryFailureMessageExplanation}
      />
    ),
  },
];

interface Props {
  apiRef?: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
}

const EmailDeliveryLogGrid = ({ apiRef, dataSource }: Props) => {
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const handleDataSourceError = useCallback((error: Error) => {
    logError(error, "[EmailDeliveryLogGrid]");
    setDataSourceError(error);
  }, []);

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading emails failed" />;
  }

  return (
    <DataGrid<SentEmail>
      apiRef={apiRef}
      columns={columnDefinitions}
      lazyLoading
      dataSource={dataSource}
      paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
      onDataSourceError={handleDataSourceError}
      multilineRows
      noRowsText="No emails"
      disableColumnSorting
      disableColumnReorder
    />
  );
};

export default EmailDeliveryLogGrid;
